/**
 * Spec 014a Phase D / 014b: what each brand host switch hides on the web.
 * The server refuses the writes anyway; this only keeps hosts from seeing
 * buttons that would fail. A brand admin (or the platform owner) still sees
 * them, marked "Admin only" (014b US-1). Plain functions, tested in
 * `hostCapabilities.test.ts`.
 */

/** Page and trip switches the brand admin turns on and off. */
export const STORED_CAPABILITY_KEYS = [
  'tripFees',
  'insurance',
  'couponsAndDealerTools',
  'bookingActions',
  'refundReview',
  'incidentAmounts',
  'licenceReview',
] as const;

/** Car settings: true while the host sets the value, false when the brand sets it. */
export const CAR_SETTING_KEYS = [
  'carHourlyPricing',
  'carMonthlyPricing',
  'carDiscounts',
  'carProtectionDecline',
  'carAgeLimit',
  'carTripPreferences',
  'carDistanceLimits',
  'carDeposit',
  'carGuestInstructions',
  // Spec 014c: false when the brand does not offer airport delivery.
  'carDelivery',
] as const;

/** Spec 014a's groups; the server still sends them for older clients. */
export const LEGACY_CAPABILITY_KEYS = ['carPricing', 'carRules', 'protectionAndFeesPages'] as const;

export const HOST_CAPABILITY_KEYS = [
  ...STORED_CAPABILITY_KEYS,
  ...CAR_SETTING_KEYS,
  ...LEGACY_CAPABILITY_KEYS,
] as const;

export type StoredCapabilityKey = (typeof STORED_CAPABILITY_KEYS)[number];
export type CarSettingKey = (typeof CAR_SETTING_KEYS)[number];
export type HostCapabilityKey = (typeof HOST_CAPABILITY_KEYS)[number];
export type HostCapabilities = Record<HostCapabilityKey, boolean>;

/** What the page assumes while loading or signed out: everything on, like today. */
export const ALL_CAPABILITIES_ON: HostCapabilities = HOST_CAPABILITY_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: true }),
  {} as HostCapabilities,
);

export function isCarSettingKey(key: string): key is CarSettingKey {
  return (CAR_SETTING_KEYS as readonly string[]).includes(key);
}

/** Host pages a switch turns off; their links are hidden and their URLs redirect. */
export const HOST_PAGE_CAPABILITY: Readonly<Record<string, HostCapabilityKey>> = {
  '/cars/host/protection-plans': 'insurance',
  '/cars/host/miscellaneous-fees': 'tripFees',
  '/cars/host/coupons': 'couponsAndDealerTools',
  '/cars/host/product-type-details': 'couponsAndDealerTools',
  '/manage-short-id': 'couponsAndDealerTools',
  '/dealership': 'couponsAndDealerTools',
  '/invoices/refund-status': 'refundReview',
};

/** `ViewCarPage` sections a car setting hides (`?section=`). The rest of the page has no switch. */
export const CAR_SECTION_CAPABILITY: Readonly<Record<string, CarSettingKey>> = {
  hourlyPricing: 'carHourlyPricing',
  monthlyPricing: 'carMonthlyPricing',
  discounts: 'carDiscounts',
  // The "guests may decline protection" toggle (`isProtectionDeclinable`).
  protectionPlan: 'carProtectionDecline',
  ageLimit: 'carAgeLimit',
  tripPreferences: 'carTripPreferences',
  distanceLimit: 'carDistanceLimits',
  depositAmount: 'carDeposit',
  guestInstructions: 'carGuestInstructions',
  deliveryLocations: 'carDelivery',
};

export const CAPABILITY_LABELS: Readonly<Record<HostCapabilityKey, string>> = {
  tripFees: 'trip fees',
  insurance: 'protection plans',
  couponsAndDealerTools: 'coupons and dealer tools',
  bookingActions: 'trip changes and cancellations',
  refundReview: 'refund review',
  incidentAmounts: 'incident charges',
  licenceReview: 'guest licence review',
  carHourlyPricing: 'hourly pricing',
  carMonthlyPricing: 'monthly pricing',
  carDiscounts: 'discounts',
  carProtectionDecline: 'whether guests may decline protection',
  carAgeLimit: 'the minimum driver age',
  carTripPreferences: 'trip preferences',
  carDistanceLimits: 'distance limits',
  carDeposit: 'the security deposit',
  carGuestInstructions: 'guest instructions',
  carDelivery: 'delivery locations',
  carPricing: 'discounts and special pricing',
  carRules: 'deposits, mileage, age and trip rules',
  protectionAndFeesPages: 'protection plans and trip fees',
};

/** How an entry shows: as usual, marked for an admin, or not at all. */
export type CapabilityView = 'show' | 'adminOnly' | 'hidden';

/**
 * Spec 014b US-1: a switched-off entry is hidden from hosts; a brand admin
 * (or the platform owner) still sees it, marked "Admin only".
 */
export function capabilityView(
  capabilities: HostCapabilities,
  capability: HostCapabilityKey,
  isAdmin: boolean,
): CapabilityView {
  if (capabilities[capability]) return 'show';
  return isAdmin ? 'adminOnly' : 'hidden';
}

function pageCapability(path: string): HostCapabilityKey | null {
  const key = Object.keys(HOST_PAGE_CAPABILITY).find(
    page => path === page || path.startsWith(`${page}/`),
  );
  return key ? HOST_PAGE_CAPABILITY[key] : null;
}

export function hostPageView(
  path: string,
  capabilities: HostCapabilities,
  isAdmin = false,
): CapabilityView {
  const capability = pageCapability(path);
  return capability ? capabilityView(capabilities, capability, isAdmin) : 'show';
}

export function isHostPageAllowed(
  path: string,
  capabilities: HostCapabilities,
  isAdmin = false,
): boolean {
  return hostPageView(path, capabilities, isAdmin) !== 'hidden';
}

export function carSectionView(
  section: string,
  capabilities: HostCapabilities,
  isAdmin = false,
): CapabilityView {
  const capability = CAR_SECTION_CAPABILITY[section];
  return capability ? capabilityView(capabilities, capability, isAdmin) : 'show';
}

export function isCarSectionAllowed(
  section: string,
  capabilities: HostCapabilities,
  isAdmin = false,
): boolean {
  return carSectionView(section, capabilities, isAdmin) !== 'hidden';
}

/**
 * FR-028: "Lenvoy manages trip fees on this site." A car setting the brand
 * took comes with its value: "Lenvoy sets distance limits on this site." A
 * host approved on another brand gets the same line: nothing points to the
 * other site. Delivery is not set by the brand, only not offered (spec 014c).
 */
export function managedByText(capability: HostCapabilityKey, brandName: string): string {
  if (capability === 'carDelivery') {
    return `${brandName} does not offer delivery on this site.`;
  }
  const verb = isCarSettingKey(capability) ? 'sets' : 'manages';
  return `${brandName} ${verb} ${CAPABILITY_LABELS[capability]} on this site.`;
}

/**
 * Server answers can miss a key: a missing switch reads as on. An API from
 * before spec 014b sends only the grouped keys, so a new key it lacks follows
 * its group (a page follows `protectionAndFeesPages`, a car setting
 * `carPricing` or `carRules`).
 */
export function normalizeCapabilities(
  value: Partial<Record<string, unknown>> | null | undefined,
): HostCapabilities {
  const read = (key: string): boolean | undefined =>
    typeof value?.[key] === 'boolean' ? (value[key] as boolean) : undefined;
  const legacyGroup: Partial<Record<HostCapabilityKey, string>> = {
    tripFees: 'protectionAndFeesPages',
    insurance: 'protectionAndFeesPages',
    carHourlyPricing: 'carPricing',
    carMonthlyPricing: 'carPricing',
    carDiscounts: 'carPricing',
    carProtectionDecline: 'carRules',
    carAgeLimit: 'carRules',
    carTripPreferences: 'carRules',
    carDistanceLimits: 'carRules',
    carDeposit: 'carRules',
  };
  return HOST_CAPABILITY_KEYS.reduce((acc, key) => {
    const own = read(key);
    const group = legacyGroup[key];
    acc[key] = own ?? (group ? read(group) : undefined) ?? true;
    return acc;
  }, {} as HostCapabilities);
}
